import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import type {ComposerTranslation} from 'vue-i18n'
import {IMessageAction} from '@toolify/client/src/modules/notifications/types/IMessageAction'
import {MessageActionKindType} from '@toolify/client/src/modules/notifications/enum/MessageActionKindType'

interface IWorkspaceBillingBlockedMessageMeta {
  messageKey: string
  linkLabelKey: string
  getHref: (workspaceName: string) => string
}

export interface IWorkspaceBillingBlockedMessage {
  text: string
  // null when no workspace is loaded, so there is nowhere to link to
  action: IMessageAction | null
}

export function getWorkspaceBillingBlockedCollapseKey(errorName: KnownError): string {
  return `billing.blocked:${errorName}`
}

export function getWorkspaceUpgradeHref(workspaceName: string): string {
  return `/@${workspaceName}/upgrade`
}

export function getWorkspacePaymentMethodsHref(workspaceName: string): string {
  return `/@${workspaceName}/settings/billing/payment-methods`
}

export function getWorkspaceBudgetsAndAlertsHref(workspaceName: string): string {
  return `/@${workspaceName}/settings/billing/budgets-and-alerts`
}

export function getWorkspaceBillingUsageHref(workspaceName: string): string {
  return `/@${workspaceName}/settings/billing/usage`
}

export function getWorkspaceGroupsHref(workspaceName: string): string {
  return `/@${workspaceName}/settings/groups`
}

const messageMetaByErrorName: Partial<Record<KnownError, IWorkspaceBillingBlockedMessageMeta>> = {
  [KnownError.WORKSPACE_SUBSCRIPTION_REQUIRED]: {
    messageKey: 'errors.workspaceSubscriptionRequired',
    linkLabelKey: 'workspaceUpgrade.upgradePlan',
    getHref: getWorkspaceUpgradeHref,
  },
  [KnownError.WORKSPACE_SUSPENDED]: {
    messageKey: 'errors.workspaceSuspended',
    linkLabelKey: 'billing.suspendedPaywall.changePaymentMethodCta',
    getHref: getWorkspacePaymentMethodsHref,
  },
  [KnownError.USAGE_LIMIT_EXCEEDED]: {
    messageKey: 'errors.workspaceUsageLimitExceeded',
    linkLabelKey: 'workspaces.settings.billingBudgetsAndAlerts',
    getHref: getWorkspaceBudgetsAndAlertsHref,
  },
  [KnownError.ON_DEMAND_USAGE_BLOCKED]: {
    messageKey: 'errors.workspaceOnDemandUsageBlocked',
    linkLabelKey: 'billing.suspendedPaywall.changePaymentMethodCta',
    getHref: getWorkspacePaymentMethodsHref,
  },
  [KnownError.SUBSCRIPTION_PAID_MANUALLY]: {
    messageKey: 'errors.workspacePaidManually',
    linkLabelKey: 'billing.manual.openPlanCta',
    getHref: getWorkspaceBillingUsageHref,
  },
  [KnownError.SUBSCRIPTION_ON_CUSTOM_PLAN]: {
    messageKey: 'errors.workspaceOnCustomPlan',
    linkLabelKey: 'billing.manual.openPlanCta',
    getHref: getWorkspaceBillingUsageHref,
  },
}

// In-app billing pages open through the router, like any other link action with `to`
export function getBillingLinkAction(href: string | null, label: string): IMessageAction | null {
  if (!href) {
    return null
  }
  return {
    label,
    kind: MessageActionKindType.Link,
    to: href,
  }
}

export function getWorkspaceBillingBlockedMessage(
  errorName: KnownError,
  workspaceName: string | null | undefined,
  t: ComposerTranslation,
): IWorkspaceBillingBlockedMessage | null {
  const meta = messageMetaByErrorName[errorName]
  if (!meta) {
    return null
  }
  return {
    text: t(meta.messageKey),
    action: getBillingLinkAction(workspaceName ? meta.getHref(workspaceName) : null, t(meta.linkLabelKey)),
  }
}
